import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css, SerializedStyles } from '@emotion/react';

import DropDownList from "./DropDownList";
import theme from "../../../theme";
import DropdownChevron from "./DropdownChevron";

export type DropdownOption = {
    id: string,
    value: string,
}

type Props = {
    className?: string,
    extraChevronStyles?: SerializedStyles,
    extraOptionContainerStyles?: SerializedStyles,
    extraOptionStyles?: SerializedStyles,
    extraSelectedStyles?: SerializedStyles,
    hasBorder?: boolean,
    hasError?: boolean,
    options?: Array<DropdownOption>,
    placeholder?: string,
    extraPlaceholderStyles?: SerializedStyles,
    readOnly?: boolean,
    searchable?: boolean,
    selected?: DropdownOption,
    setSelected?: Function
};

const DropDown: React.FC<Props> = ({
    className,
    extraChevronStyles,
    extraOptionContainerStyles,
    extraOptionStyles,
    extraSelectedStyles,
    hasBorder,
    hasError,
    options,
    placeholder,
    extraPlaceholderStyles,
    readOnly,
    searchable = false,
    selected,
    setSelected,
}) => {
    const [dropped, setDropped] = useState(false);
    const dropdownRef = useRef<HTMLInputElement>(null);

    const toggle = useCallback(()=> {
        if (readOnly) return;
        setDropped(dropped => !dropped);
    }, [setDropped, readOnly]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropped(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    return (
        <div css={containerStyle(dropped)} ref={dropdownRef} className={className} onClick={toggle}>
            <div css={selectedContainerStyle(hasBorder, hasError, readOnly, dropped)} className={'placeholder-container'}>
                {selected
                    ? (<div css={selectedItemStyle(extraSelectedStyles)}>{selected.value}</div>)
                    : (<div css={placeholderStyle(extraPlaceholderStyles)}>{placeholder && placeholder}</div>)
                }
                {!readOnly && (
                    <DropdownChevron dropped={dropped} hasError={hasError} css={dropdownChevronStyle(extraChevronStyles)} />
                )}
            </div>
            {dropped && (
                <DropDownList
                    extraOptionContainerStyles={extraOptionContainerStyles}
                    extraOptionStyles={extraOptionStyles}
                    selected={selected}
                    setSelected={setSelected}
                    setDropped={setDropped}
                    options={options}
                    searchable={searchable}
                />
            )}
        </div>
    );
};

DropDown.defaultProps = {
    hasError: false,
    hasBorder: true,
    readOnly: false,
    options: [],
    setSelected: () => {},
}

const containerStyle = (dropped?: boolean) => css`
    display: flex;
    flex-direction: row;
    position: relative;
    width: 100%;
    
    flex-grow: 1;
    
    ${dropped && css`
        background: white;
    `}
`;

const selectedContainerStyle = (hasBorder?: boolean, hasError?: boolean, readOnly?: boolean, dropped?: boolean) => css`
    border: 1px solid ${hasBorder ? theme.forms.defaults.borderColor : theme.colours.transparent};
    position: relative;
    z-index: 2;
    width: 100%;
    display: flex;
    ${hasError && theme.forms.error};

    ${!readOnly && css`
        :hover {
            cursor: pointer;
        }
    `};
`;

const selectedItemStyle = (extraSelectedStyles?: SerializedStyles) => css`
    padding: 4px;
    font-size: 18px;
    ${extraSelectedStyles};
`;

const placeholderStyle = (extraPlaceholderStyles?: SerializedStyles) => css`
    font-size: 18px;
    ${theme.mixins.placeholderAttributes()};

    ${extraPlaceholderStyles};
`;

const dropdownChevronStyle = (extraChevronStyles?: SerializedStyles) => css`
    right: 15px;

    ${extraChevronStyles};
`;

export default DropDown;
